/**
 * Экран «Уведомления»: что бот присылает в Telegram сам.
 *
 * Переключателей два — «конец отдыха» и «сообщение дня». Больше бот первым
 * не пишет: ответы на команды и файл для тренера человек просит сам, и
 * выключать их здесь было бы странно.
 *
 * Выключенное не ломает остального. Отсчёт отдыха на экране тренировки идёт
 * как раньше, сообщение дня по-прежнему лежит на «Сегодня» — молчит только
 * чат. Проверяет настройку сервер в момент отправки, поэтому она действует
 * сразу, в том числе на уже идущий отдых.
 *
 * Тумблер переключается сразу, не дожидаясь ответа: так ведёт себя любой
 * системный переключатель. Если сервер не принял — тумблер возвращается
 * назад, а под ним появляется причина. Молча оставить его в новом положении
 * значило бы показать настройку, которой на самом деле нет.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'

import { ApiError, NetworkError, api } from '../api/client'
import type { NotificationSettings } from '../api/types'
import { haptic } from '../auth/telegram'
import { IconChevronLeft } from '../components/icons'
import { ErrorBox, Loader } from '../components/ui'
import { useAsync } from '../hooks'

type Kind = 'rest_timer' | 'daily_message'

export default function Notifications() {
  const { data, error, loading, reload, set } = useAsync(() =>
    api.get<NotificationSettings>('/notifications'),
  )
  const [saving, setSaving] = useState<Kind | null>(null)
  const [failure, setFailure] = useState('')

  async function toggle(kind: Kind): Promise<void> {
    // Второй тап, пока первый летит, — не новая команда, а дребезг: ответ
    // первого всё равно перепишет состояние.
    if (!data || saving) return
    const next = !data[kind]
    haptic('light')
    setFailure('')
    setSaving(kind)
    set({ ...data, [kind]: next })
    try {
      set(await api.patch<NotificationSettings>('/notifications', { [kind]: next }))
    } catch (e) {
      set((prev) => (prev ? { ...prev, [kind]: !next } : prev))
      setFailure(
        e instanceof NetworkError
          ? 'Нет связи — настройка не сохранилась. Попробуй ещё раз.'
          : e instanceof ApiError
            ? e.message
            : 'Настройка не сохранилась. Попробуй ещё раз.',
      )
    } finally {
      setSaving(null)
    }
  }

  const back = (
    <Link to="/" className="btn ghost prg-back">
      <IconChevronLeft size={22} />
      Сегодня
    </Link>
  )

  if (loading && !data) {
    return (
      <div className="screen">
        {back}
        <Loader />
      </div>
    )
  }

  if (!data) {
    return (
      <div className="screen">
        {back}
        <h1>Уведомления</h1>
        <ErrorBox error={error} onRetry={() => void reload()} />
      </div>
    )
  }

  return (
    <div className="screen">
      {back}
      <h1>Уведомления</h1>
      <p className="muted ntf-lead">Что бот присылает в Telegram сам.</p>

      {!data.bot_connected && (
        <div className="stale-note">
          Бот сейчас не подключён к сервису — сообщения не придут, даже включённые.
        </div>
      )}

      <Switch
        id="ntf-rest"
        title="Конец отдыха"
        note="Бот напишет, когда пора делать следующий подход. Отсчёт на экране тренировки идёт и без этого."
        on={data.rest_timer}
        onToggle={() => void toggle('rest_timer')}
      />
      <Switch
        id="ntf-daily"
        title="Сообщение дня"
        note={`Каждый день в ${data.daily_message_time}. Тот же текст всегда есть на экране «Сегодня».`}
        on={data.daily_message}
        onToggle={() => void toggle('daily_message')}
      />

      {failure && <div className="se-fail">{failure}</div>}

      <p className="faint ntf-foot">
        Ответы на команды и файл для тренера приходят всегда — их ты просишь сам.
      </p>
    </div>
  )
}

/**
 * Строка-переключатель во всю ширину: в тумблер шириной в палец не целятся.
 * Пояснение привязано через `aria-describedby` — диктор прочтёт его вместе
 * с названием.
 */
function Switch({
  id,
  title,
  note,
  on,
  onToggle,
}: {
  /** id пояснения: в `aria-describedby` идёт список через пробел, и
   *  название с пробелом в роли id развалилось бы на два. */
  id: string
  title: string
  note: string
  on: boolean
  onToggle: () => void
}) {
  const noteId = `${id}-note`
  return (
    <>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-describedby={noteId}
        className="switch-row"
        onClick={onToggle}
      >
        <span>{title}</span>
        <span className="switch" aria-hidden="true" />
      </button>
      <div id={noteId} className="faint ntf-note">
        {note}
      </div>
    </>
  )
}
